import React, { useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Box,
  Typography,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * LanguageSwitcher Component
 * Dropdown menu for selecting application language
 *
 * Usage:
 * <LanguageSwitcher />
 */

const LanguageSwitcher = ({ edge, color = 'inherit', ...props }) => {
  const { language, setLanguage, languages, t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    setLanguage(langCode);
    handleClose();
  };

  return (
    <>
      <Tooltip title={t('settings.language') || 'Language'}>
        <IconButton
          onClick={handleClick}
          color={color}
          edge={edge}
          aria-label="Select language"
          aria-controls={open ? 'language-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          sx={{
            transition: 'transform 0.3s ease',
            '&:hover': {
              transform: 'scale(1.1)',
            },
          }}
          {...props}
        >
          <LanguageIcon />
        </IconButton>
      </Tooltip>

      <Menu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 180,
            mt: 1,
          },
        }}
      >
        {Object.entries(languages).map(([code, lang]) => (
          <MenuItem
            key={code}
            onClick={() => handleLanguageChange(code)}
            selected={language === code}
            sx={{
              display: 'flex',
              gap: 1,
              py: 1.5,
            }}
          >
            <Box sx={{ fontSize: '1.5rem', mr: 1 }}>
              {lang.flag}
            </Box>
            <ListItemText
              primary={lang.name}
              primaryTypographyProps={{
                fontWeight: language === code ? 600 : 400,
              }}
            />
            {language === code && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                <CheckIcon fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default LanguageSwitcher;
